import { KeyboardEvent, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Big from 'big.js';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import LoadTable, { LoadTableColumn } from '../../../../../components/novarium/LoadTable';
import { AddRowButton, FieldError } from '../../../../../components/novarium/NvTableParts';
import { CashRegisterFinancialAccountDTO } from '../../../../../dto/locationcash/LocationCashDTO';
import useNotify from '../../../../../hooks/useNotify';
import { parseApiError } from '../../../../../services/apiError';
import { getCashRegisterFinancialAccountsByPaymentMethod } from '../../../../../services/LocationCashController';
import { formatMoney } from '../../../../../utils/money';
import { getPaymentOptions, getPendingInvoices, registerPatientPayment } from '../../../services/AgendaService';
import { AvailableCheck, CheckRequest, PaymentOptions, PendingInvoice } from '../../../types/billing.types';
import { errorMessageOf, fieldErrorsOf, firstErrorWithPrefix, formatShortDate, newRowKey, toApiDate, toBig } from '../billingUtils';
import CheckDialog, { NewCheck } from './CheckDialog';
import CheckPickerDialog from './CheckPickerDialog';
import TaxDialog, { NewTax } from './TaxDialog';

export interface PaymentStepResult {
  receiptId: number;
  invoices: PendingInvoice[];
}

interface PaymentStepProps {
  patientId: number;
  /** Facturas a preseleccionar (la recién facturada); sin ninguna, se eligen todas las que se pueden cobrar. */
  preselectedInvoiceIds: number[];
  exitRef?: MutableRefObject<(() => void) | null>;
  /** Null: se salió sin cobrar. */
  onClose: (result: PaymentStepResult | null) => void;
}

interface Line {
  rowKey: string;
  paymentMethodId: number;
  methodName: string;
  amount: number;
  financialAccountId: number | null;
  financialAccountName: string | null;
}

interface CheckLine {
  rowKey: string;
  /** De cartera. */
  checkId?: number;
  number: string;
  bank: string;
  amount: number;
  paymentDate: string | null;
  status: string;
  destination: string;
  /** Nuevo: se graba con el recibo. */
  newCheck?: NewCheck;
}

interface TaxLine extends NewTax {
  rowKey: string;
}

type AddErrors = Partial<Record<'method' | 'amount' | 'account', string>>;

function defaultAccountOf(accounts: CashRegisterFinancialAccountDTO[]): CashRegisterFinancialAccountDTO | null {
  return accounts.length === 1 ? accounts[0] : accounts.find((a) => a.isDefault) ?? null;
}

/**
 * Paso "Recibo" del cobro del turno, con las secciones de la ventana de pago de Vaadin ({@code CustomerRegisterPaymentWindow}):
 * comprobantes a imputar, caja (si el usuario tiene una abierta), medios de pago con su cuenta, cheques e impuestos
 * relacionados, y el aviso de pago parcial o saldo a favor antes de registrar.
 */
export default function PaymentStep({ patientId, preselectedInvoiceIds, exitRef, onClose }: PaymentStepProps) {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<PaymentOptions | null>(null);
  const [invoices, setInvoices] = useState<PendingInvoice[]>([]);
  const [imputations, setImputations] = useState<Record<number, { checked: boolean; amount: number | null }>>({});
  const [registerId, setRegisterId] = useState<number | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [checks, setChecks] = useState<CheckLine[]>([]);
  const [taxes, setTaxes] = useState<TaxLine[]>([]);
  const [barcode, setBarcode] = useState('');

  // Fila de alta de medios de pago
  const [methodId, setMethodId] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [accounts, setAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
  const [addErrors, setAddErrors] = useState<AddErrors>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const [dialog, setDialog] = useState<'check' | 'picker' | 'tax' | null>(null);
  const [errors, setErrors] = useState<{ invoices?: string; register?: string; lines?: string }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getPaymentOptions(), getPendingInvoices(patientId)])
      .then(async ([paymentOptions, pending]) => {
        if (cancelled) return;
        setOptions(paymentOptions);
        setInvoices(pending);
        const preselected = new Set(preselectedInvoiceIds);
        const initial: Record<number, { checked: boolean; amount: number | null }> = {};
        for (const invoice of pending) {
          initial[invoice.invoiceId] = { checked: invoice.payable && (preselected.size === 0 || preselected.has(invoice.invoiceId)), amount: invoice.pendingAmount };
        }
        setImputations(initial);
        const register = paymentOptions.cashRegisters.find((r) => r.isDefault)?.id ?? null;
        setRegisterId(register);

        // Como registerPaymentForAdminForm: el medio de pago es el de la factura, y la línea con el saldo se agrega
        // sola si la cuenta queda definida sin ambigüedad (sin caja, o con una sola cuenta para el medio)
        const selected = pending.filter((i) => initial[i.invoiceId]?.checked);
        const invoiceMethod = selected.map((i) => i.paymentMethodId).find((id) => paymentOptions.paymentMethods.some((m) => m.id === id));
        const method = paymentOptions.paymentMethods.find((m) => m.id === invoiceMethod) ?? paymentOptions.paymentMethods[0] ?? null;
        setMethodId(method?.id ?? null);
        const total = selected.reduce((sum, i) => sum.plus(i.pendingAmount), new Big(0));
        if (!method || total.lte(0)) return;
        if (paymentOptions.cashRegisters.length === 0) {
          setLines([{ rowKey: newRowKey('l'), paymentMethodId: method.id, methodName: method.name, amount: total.toNumber(), financialAccountId: null, financialAccountName: null }]);
          return;
        }
        if (register) {
          const registerAccounts = await getCashRegisterFinancialAccountsByPaymentMethod(register, method.id).catch(() => []);
          if (cancelled) return;
          if (registerAccounts.length === 1) {
            setLines([{
              rowKey: newRowKey('l'), paymentMethodId: method.id, methodName: method.name, amount: total.toNumber(),
              financialAccountId: registerAccounts[0].financialAccountId, financialAccountName: registerAccounts[0].financialAccountName,
            }]);
            return;
          }
        }
        setAmount(total.toNumber());
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        notify.error(errorMessageOf(parseApiError(error), 'No se pudieron cargar los datos del cobro. Intentá de nuevo.'));
        onClose(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Se carga una vez al abrir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methodOf = (id: number | null) => options?.paymentMethods.find((m) => m.id === id) ?? null;

  // Cuentas de la caja para el medio de la fila de alta
  useEffect(() => {
    if (!registerId || !methodId) {
      setAccounts([]);
      setAccountId(null);
      return undefined;
    }
    let cancelled = false;
    getCashRegisterFinancialAccountsByPaymentMethod(registerId, methodId)
      .then((list) => {
        if (cancelled) return;
        setAccounts(list ?? []);
        setAccountId((current) => (current && list.some((a) => a.financialAccountId === current) ? current
          : defaultAccountOf(list ?? [])?.financialAccountId ?? null));
      })
      .catch((error: unknown) => {
        parseApiError(error);
        if (!cancelled) setAccounts([]);
      });
    return () => { cancelled = true; };
  }, [registerId, methodId]);

  const imputed = useMemo(() => invoices.reduce(
    (sum, i) => (imputations[i.invoiceId]?.checked ? sum.plus(toBig(imputations[i.invoiceId]?.amount)) : sum), new Big(0),
  ), [invoices, imputations]);
  const totalMethods = useMemo(() => lines.reduce((sum, l) => sum.plus(l.amount), new Big(0)), [lines]);
  const totalChecks = useMemo(() => checks.reduce((sum, c) => sum.plus(c.amount), new Big(0)), [checks]);
  const totalTaxes = useMemo(() => taxes.reduce((sum, t) => sum.plus(t.amount), new Big(0)), [taxes]);
  const paid = totalMethods.plus(totalChecks).plus(totalTaxes);
  const usesRegister = (options?.cashRegisters.length ?? 0) > 0;
  const payableCount = invoices.filter((i) => i.payable).length;

  /** Como Vaadin: al cambiar de caja, cada línea conserva su cuenta si sigue valiendo, o toma la de la caja nueva. */
  async function changeRegister(newRegisterId: number | null) {
    setRegisterId(newRegisterId);
    setErrors((e) => ({ ...e, register: undefined, lines: undefined }));
    if (!newRegisterId || lines.length === 0) {
      setLines((current) => current.map((l) => ({ ...l, financialAccountId: null, financialAccountName: null })));
      return;
    }
    try {
      const byMethod = new Map<number, CashRegisterFinancialAccountDTO[]>();
      await Promise.all(Array.from(new Set(lines.map((l) => l.paymentMethodId))).map(async (id) => {
        byMethod.set(id, await getCashRegisterFinancialAccountsByPaymentMethod(newRegisterId, id) ?? []);
      }));
      setLines((current) => current.map((l) => {
        const available = byMethod.get(l.paymentMethodId) ?? [];
        if (l.financialAccountId && available.some((a) => a.financialAccountId === l.financialAccountId)) return l;
        const account = defaultAccountOf(available);
        return { ...l, financialAccountId: account?.financialAccountId ?? null, financialAccountName: account?.financialAccountName ?? null };
      }));
    } catch (error: unknown) {
      parseApiError(error);
      setLines((current) => current.map((l) => ({ ...l, financialAccountId: null, financialAccountName: null })));
    }
  }

  const remaining = () => imputed.minus(paid);

  function resetAddRow(nextAmount: Big) {
    setAmount(nextAmount.gt(0) ? nextAmount.toNumber() : null);
    setAddErrors({});
    setEditingKey(null);
  }

  function submitAddRow() {
    const validation: AddErrors = {};
    const method = methodOf(methodId);
    if (!method) validation.method = 'Elegí el medio de pago';
    if (amount == null || amount <= 0) validation.amount = 'Ingresá un importe mayor a 0';
    if (registerId && !accountId) validation.account = accounts.length === 0 ? 'La caja no tiene una cuenta para este medio' : 'Elegí la cuenta';
    setAddErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const account = accounts.find((a) => a.financialAccountId === accountId) ?? null;
    const line: Line = {
      rowKey: editingKey ?? newRowKey('l'),
      paymentMethodId: method!.id,
      methodName: method!.name,
      amount: amount!,
      financialAccountId: registerId ? account?.financialAccountId ?? null : null,
      financialAccountName: registerId ? account?.financialAccountName ?? null : null,
    };
    const nextLines = editingKey ? lines.map((l) => (l.rowKey === editingKey ? line : l)) : [...lines, line];
    setLines(nextLines);
    setErrors((e) => ({ ...e, lines: undefined }));
    resetAddRow(imputed.minus(nextLines.reduce((sum, l) => sum.plus(l.amount), new Big(0))).minus(totalChecks).minus(totalTaxes));
  }

  function handleAddRowKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitAddRow();
    } else if (event.key === 'Escape' && editingKey) {
      event.preventDefault();
      resetAddRow(remaining());
    }
  }

  /** "Cód. barras": con los 20 dígitos marca la factura cuyo id son los primeros 8. */
  function readBarcode(value: string) {
    setBarcode(value);
    if (value.length !== 20) return;
    const id = Number.parseInt(value.substring(0, 8), 10);
    const invoice = invoices.find((i) => i.invoiceId === id);
    if (!invoice || !invoice.payable) {
      notify.error('No se encontró el comprobante: revisá que sea del paciente y que tenga saldo.');
    } else {
      setImputations((c) => ({ ...c, [id]: { ...c[id], checked: true } }));
      setErrors((e) => ({ ...e, invoices: undefined }));
    }
    setBarcode('');
  }

  function addPortfolioChecks(selected: AvailableCheck[]) {
    setChecks((current) => [...current, ...selected.map((c) => ({
      rowKey: newRowKey('k'), checkId: c.checkId, number: c.number, bank: c.bank, amount: c.amount, paymentDate: c.paymentDate,
      status: c.status, destination: c.destination,
    }))]);
    setErrors((e) => ({ ...e, lines: undefined }));
    setDialog(null);
  }

  function addNewCheck(check: NewCheck) {
    setChecks((current) => [...current, {
      rowKey: newRowKey('k'), number: check.number, bank: check.bank, amount: check.amount, paymentDate: toApiDate(check.paymentDate),
      status: options?.checkStatuses.find((s) => s.id === check.status)?.name ?? '',
      destination: options?.checkDestinations.find((d) => d.id === check.destination)?.name ?? '',
      newCheck: check,
    }]);
    setErrors((e) => ({ ...e, lines: undefined }));
    setDialog(null);
  }

  function addTax(tax: NewTax) {
    setTaxes((current) => [...current, { ...tax, rowKey: newRowKey('t') }]);
    setErrors((e) => ({ ...e, lines: undefined }));
    setDialog(null);
  }

  function validateBeforeSubmit(): boolean {
    const next: { invoices?: string; register?: string; lines?: string } = {};
    const selected = invoices.filter((i) => imputations[i.invoiceId]?.checked);
    if (selected.length === 0) next.invoices = 'Elegí al menos un comprobante';
    const empty = selected.find((i) => !(imputations[i.invoiceId]?.amount && imputations[i.invoiceId]!.amount! > 0));
    const overPending = selected.find((i) => toBig(imputations[i.invoiceId]?.amount).gt(i.pendingAmount));
    if (empty) next.invoices = `Ingresá cuánto imputar al comprobante ${empty.formNumber}`;
    else if (overPending) next.invoices = `El importe no puede superar el pendiente del comprobante ${overPending.formNumber}`;
    if (usesRegister && !registerId) next.register = 'Elegí la caja donde se registra el pago';
    if (lines.length + checks.length + taxes.length === 0) next.lines = 'Ingresá al menos un medio de pago';
    else if (registerId && lines.some((l) => !l.financialAccountId)) next.lines = 'Hay medios de pago sin cuenta: editalos y elegí la cuenta';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function save() {
    setSaving(true);
    try {
      const selected = invoices.filter((i) => imputations[i.invoiceId]?.checked);
      const response = await registerPatientPayment({
        locationCashRegisterId: registerId,
        invoices: selected.map((i) => ({ invoiceId: i.invoiceId, amount: imputations[i.invoiceId]!.amount! })),
        paymentLines: lines.map((l) => ({ paymentMethodId: l.paymentMethodId, amount: l.amount, financialAccountId: l.financialAccountId })),
        checks: checks.map((c): CheckRequest => (c.checkId ? { checkId: c.checkId } : {
          number: c.newCheck!.number,
          bank: c.newCheck!.bank,
          amount: c.newCheck!.amount,
          paymentDate: toApiDate(c.newCheck!.paymentDate),
          description: c.newCheck!.description || undefined,
          status: c.newCheck!.status,
          destination: c.newCheck!.destination,
        })),
        taxes: taxes.map((t) => ({ taxId: t.taxId, amount: t.amount, description: t.description })),
      });
      notify.success('Pago registrado');
      onClose({ receiptId: response.paymentReceiptId, invoices: selected });
    } catch (error: unknown) {
      const apiError = parseApiError(error);
      const fieldErrors = fieldErrorsOf(apiError);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors({
          invoices: firstErrorWithPrefix(fieldErrors, 'invoices'),
          register: fieldErrors.locationCashRegisterId,
          lines: firstErrorWithPrefix(fieldErrors, 'paymentLines') ?? firstErrorWithPrefix(fieldErrors, 'checks')
            ?? firstErrorWithPrefix(fieldErrors, 'taxes'),
        });
      } else {
        notify.error(errorMessageOf(apiError, 'No se pudo registrar el pago. Intentá de nuevo.'));
      }
    } finally {
      setSaving(false);
    }
  }

  /** Como Vaadin: si lo cobrado no coincide con lo imputado, se confirma el pago parcial o el saldo a favor. */
  function handleCollect() {
    if (!validateBeforeSubmit()) return;
    const difference = paid.minus(imputed);
    if (difference.eq(0)) {
      void save();
      return;
    }
    const partial = difference.lt(0);
    confirmDialog({
      header: partial ? '¿Registrar un pago parcial?' : '¿Registrar un saldo a favor?',
      message: partial
        ? `Lo que se cobra es menor que lo imputado: quedan ${formatMoney(difference.abs())} pendientes.`
        : `Lo que se cobra es mayor que lo imputado: quedan ${formatMoney(difference)} a favor del paciente.`,
      className: 'nv-scope nv-confirm',
      footer: (o) => (
        <>
          <Button type="button" className="buttonMain2" outlined label="Revisar" onClick={o.reject} />
          <Button type="button" className="buttonMain" label={partial ? 'Registrar pago parcial' : 'Registrar saldo a favor'} onClick={o.accept} />
        </>
      ),
      accept: () => void save(),
    });
  }

  const leave = useCallback(() => {
    confirmDialog({
      header: '¿Salir sin cobrar?',
      message: 'La factura queda impaga; la podés cobrar desde el turno.',
      className: 'nv-scope nv-confirm',
      footer: (o) => (
        <>
          <Button type="button" link label="Seguir cobrando" className="nv-push-left" onClick={o.reject} />
          <Button type="button" className="buttonMain2" outlined label="Salir sin cobrar" onClick={o.accept} />
        </>
      ),
      accept: () => onClose(null),
    });
  }, [onClose]);

  useEffect(() => {
    if (exitRef) exitRef.current = leave;
  }, [exitRef, leave]);

  if (loading || !options) {
    return <div className="nv-loading" role="status"><ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…</div>;
  }

  const lineColumns: LoadTableColumn<Line>[] = [
    {
      key: 'method',
      header: 'Medio de pago',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <Dropdown panelClassName="nv-scope" value={methodId} options={options.paymentMethods} optionLabel="name" optionValue="id" placeholder="Seleccionar"
            onChange={(e) => { setMethodId(e.value ?? null); setAddErrors({}); }}
            className={classNames('nv-in-cell', { 'p-invalid': addErrors.method })} aria-label="Medio de pago" />
          <FieldError id="payment-add-method-error" message={addErrors.method} />
        </div>
      ),
      body: (row) => row.methodName,
    },
    ...(registerId ? [{
      key: 'account',
      header: 'Cuenta',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <Dropdown panelClassName="nv-scope" value={accountId} options={accounts} optionLabel="financialAccountName" optionValue="financialAccountId"
            placeholder={accounts.length === 0 ? 'Sin cuentas para este medio' : 'Seleccionar'} disabled={accounts.length === 0}
            onChange={(e) => { setAccountId(e.value ?? null); setAddErrors((c) => ({ ...c, account: undefined })); }}
            className={classNames('nv-in-cell', { 'p-invalid': addErrors.account })} aria-label="Cuenta bancaria o financiera" />
          <FieldError id="payment-add-account-error" message={addErrors.account} />
        </div>
      ),
      body: (row: Line) => row.financialAccountName ?? '—',
    } as LoadTableColumn<Line>] : []),
    {
      key: 'amount',
      header: 'Importe',
      align: 'right',
      className: 'nv-col-money',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <InputNumber value={amount} onValueChange={(e) => { setAmount(e.value ?? null); setAddErrors((c) => ({ ...c, amount: undefined })); }}
            mode="currency" currency="ARS" locale="es-AR" minFractionDigits={2} maxFractionDigits={2} min={0}
            className={classNames('nv-number nv-in-cell', { 'p-invalid': addErrors.amount })}
            inputClassName={classNames({ 'p-filled': amount != null })} aria-label="Importe" />
          <FieldError id="payment-add-amount-error" message={addErrors.amount} />
        </div>
      ),
      body: (row) => formatMoney(row.amount),
    },
  ];

  const addAction = editingKey ? (
    <span className="nv-cell-actions">
      <Button type="button" text className="nv-icon nv-tip" icon="pi pi-check" aria-label="Confirmar cambios" data-pr-tooltip="Confirmar cambios" onClick={submitAddRow} />
      <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times" aria-label="Cancelar cambios" data-pr-tooltip="Cancelar cambios" onClick={() => resetAddRow(remaining())} />
    </span>
  ) : (
    <AddRowButton label="Agregar medio de pago" onClick={submitAddRow} />
  );

  return (
    <>

      <section className="nv-section" aria-labelledby="payment-invoices-title">
        <h2 id="payment-invoices-title" className="nv-section-title">Comprobantes a imputar el pago</h2>
        <div className="nv-form-row">
          <div className="nv-field nv-w-s">
            <label htmlFor="payment-barcode">Cód. barras</label>
            <InputText id="payment-barcode" value={barcode} maxLength={20} placeholder="Escaneá el comprobante" inputMode="numeric"
              onChange={(e) => readBarcode(e.target.value.replace(/\D/g, ''))} />
          </div>
        </div>
        <DataTable value={invoices} dataKey="invoiceId" className="nv-load-table" emptyMessage="El paciente no tiene comprobantes con saldo."
          scrollable scrollHeight="320px">
          <Column
            header={<span className="nv-sr-only">Imputar</span>}
            className="nv-col-check"
            align="center"
            body={(row: PendingInvoice) => (
              <Checkbox
                inputId={`payment-invoice-${row.invoiceId}`}
                checked={Boolean(imputations[row.invoiceId]?.checked)}
                disabled={!row.payable}
                onChange={(e) => {
                  setImputations((c) => ({ ...c, [row.invoiceId]: { ...c[row.invoiceId], checked: Boolean(e.checked) } }));
                  setErrors((er) => ({ ...er, invoices: undefined }));
                }}
                aria-label={`Imputar el pago al comprobante ${row.formNumber || row.formType}`}
              />
            )}
          />
          <Column header="Tipo" body={(row: PendingInvoice) => (
            <span className="nv-row-8">
              {row.formType}
              {!row.payable && <Tag className="nv-tag-warn nv-tip" value="Pendiente de AFIP" data-pr-tooltip="Se puede cobrar cuando AFIP la autorice" />}
            </span>
          )} />
          <Column header="Nro." align="center" alignHeader="center" body={(row: PendingInvoice) => row.formNumber || '—'} />
          <Column header="Emisor" body={(row: PendingInvoice) => row.billingEntityName ?? '—'} />
          <Column header="Punto de venta" body={(row: PendingInvoice) => row.posName ?? '—'} />
          <Column header="Fecha" className="nv-col-date" align="center" alignHeader="center" body={(row: PendingInvoice) => formatShortDate(row.date)} />
          <Column header="Descripción" body={(row: PendingInvoice) => row.description || '—'} />
          <Column header="Total" className="nv-col-money" align="right" alignHeader="right" body={(row: PendingInvoice) => formatMoney(row.amount)} />
          <Column header="Pendiente" className="nv-col-money" align="right" alignHeader="right" body={(row: PendingInvoice) => formatMoney(row.pendingAmount)} />
          <Column header="A imputar" className="nv-col-money" align="right" alignHeader="right" body={(row: PendingInvoice) => (imputations[row.invoiceId]?.checked ? (
            <InputNumber
              value={imputations[row.invoiceId]?.amount ?? null}
              onValueChange={(e) => {
                setImputations((c) => ({ ...c, [row.invoiceId]: { ...c[row.invoiceId], amount: e.value ?? null } }));
                setErrors((er) => ({ ...er, invoices: undefined }));
              }}
              mode="currency" currency="ARS" locale="es-AR" minFractionDigits={2} maxFractionDigits={2} min={0} max={row.pendingAmount}
              className="nv-number nv-in-cell" aria-label={`Importe a imputar al comprobante ${row.formNumber}`}
            />
          ) : '—')} />
        </DataTable>
        <FieldError id="payment-invoices-error" message={errors.invoices} />
      </section>

      {usesRegister && (
        <section className="nv-section" aria-labelledby="payment-register-title">
          <h2 id="payment-register-title" className="nv-section-title">Caja</h2>
          <div className="nv-form-row">
            <div className="nv-field nv-w-m">
              <label htmlFor="payment-register">Caja para registrar el pago <span className="nv-req">*</span></label>
              <Dropdown panelClassName="nv-scope" inputId="payment-register" value={registerId} options={options.cashRegisters} optionLabel="name" optionValue="id"
                placeholder="Seleccionar" onChange={(e) => changeRegister(e.value ?? null)}
                className={errors.register ? 'p-invalid' : undefined}
                aria-invalid={Boolean(errors.register)} aria-describedby={errors.register ? 'payment-register-error' : undefined} />
              <FieldError id="payment-register-error" message={errors.register} />
            </div>
          </div>
        </section>
      )}

      <section className="nv-section" aria-labelledby="payment-lines-title">
        <h2 id="payment-lines-title" className="nv-section-title">Medios de pago</h2>
        <LoadTable
          rows={lines}
          columns={lineColumns}
          addAction={addAction}
          rowActions={(row) => (
            <span className="nv-cell-actions">
              <Button type="button" text className="nv-icon nv-tip" icon="pi pi-pencil" aria-label={`Editar ${row.methodName}`} data-pr-tooltip="Editar"
                onClick={() => { setEditingKey(row.rowKey); setMethodId(row.paymentMethodId); setAmount(row.amount); setAccountId(row.financialAccountId); setAddErrors({}); }} />
              <Button type="button" text className="nv-icon nv-tip" icon="pi pi-trash" aria-label={`Quitar ${row.methodName}`} data-pr-tooltip="Quitar"
                onClick={() => { setLines((c) => c.filter((l) => l.rowKey !== row.rowKey)); if (editingKey === row.rowKey) resetAddRow(remaining()); }} />
            </span>
          )}
          emptyMessage="Sin medios de pago cargados"
          footerLabel="Total"
          footerValues={{ amount: formatMoney(totalMethods) }}
          editingRowKey={editingKey}
        />
        <FieldError id="payment-lines-error" message={errors.lines} />
      </section>

      <section className="nv-section" aria-labelledby="payment-checks-title">
        <div className="nv-section-head">
          <h2 id="payment-checks-title" className="nv-section-title">Cheques relacionados</h2>
          <div className="nv-actions-end">
            <Button type="button" className="buttonMain2" outlined icon="pi pi-plus-circle" label="Elegir de cartera" onClick={() => setDialog('picker')} />
            <Button type="button" className="buttonMain2" outlined icon="pi pi-plus-circle" label="Nuevo cheque" onClick={() => setDialog('check')} />
          </div>
        </div>
        <DataTable value={checks} dataKey="rowKey" className="nv-load-table" emptyMessage="Sin cheques cargados">
          <Column header="Número" field="number" align="center" alignHeader="center" />
          <Column header="Banco" field="bank" />
          <Column header="Fecha de cobro" className="nv-col-date" align="center" alignHeader="center" body={(c: CheckLine) => formatShortDate(c.paymentDate)} />
          <Column header="Estado" body={(c: CheckLine) => c.status || '—'} />
          <Column header="Destino" body={(c: CheckLine) => c.destination || '—'} />
          <Column header="Importe" className="nv-col-money" align="right" alignHeader="right" body={(c: CheckLine) => formatMoney(c.amount)} />
          <Column className="nv-col-actions" body={(c: CheckLine) => (
            <Button type="button" text className="nv-icon nv-tip" icon="pi pi-trash" aria-label={`Quitar el cheque ${c.number}`} data-pr-tooltip="Quitar"
              onClick={() => setChecks((current) => current.filter((x) => x.rowKey !== c.rowKey))} />
          )} />
        </DataTable>
      </section>

      <section className="nv-section" aria-labelledby="payment-taxes-title">
        <div className="nv-section-head">
          <h2 id="payment-taxes-title" className="nv-section-title">Impuestos relacionados</h2>
          <Button type="button" className="buttonMain2" outlined icon="pi pi-plus-circle" label="Nuevo impuesto" onClick={() => setDialog('tax')} />
        </div>
        <DataTable value={taxes} dataKey="rowKey" className="nv-load-table" emptyMessage="Sin impuestos cargados">
          <Column header="Impuesto" field="taxName" />
          <Column header="Descripción" field="description" />
          <Column header="Importe" className="nv-col-money" align="right" alignHeader="right" body={(t: TaxLine) => formatMoney(t.amount)} />
          <Column className="nv-col-actions" body={(t: TaxLine) => (
            <Button type="button" text className="nv-icon nv-tip" icon="pi pi-trash" aria-label={`Quitar ${t.taxName}`} data-pr-tooltip="Quitar"
              onClick={() => setTaxes((current) => current.filter((x) => x.rowKey !== t.rowKey))} />
          )} />
        </DataTable>
      </section>

      <dl className="nv-form-row">
        {[
          ['Total medio de pago', totalMethods],
          ['Total cheques', totalChecks],
          ['Total impuestos', totalTaxes],
          ['Subtotal', paid],
        ].map(([label, value]) => (
          <div key={label as string} className="nv-ro nv-w-s">
            <dt className="nv-ro-label">{label as string}</dt>
            <dd className="nv-ro-value">{formatMoney(value as Big)}</dd>
          </div>
        ))}
        <div className="nv-ro nv-w-s">
          <dt className="nv-ro-label">Total comprobantes</dt>
          <dd className="nv-ro-value nv-strong">{formatMoney(imputed)}</dd>
        </div>
      </dl>

      <footer className="nv-page-foot">
        <hr className="nv-divider" />
        <div className="nv-actions-end">
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={leave} disabled={saving} />
          <Button
            type="button"
            className="buttonMain"
            label="Cobrar"
            onClick={handleCollect}
            loading={saving}
            disabled={payableCount === 0}
            tooltip={payableCount === 0 ? 'El paciente no tiene comprobantes para cobrar' : undefined}
            tooltipOptions={{ showOnDisabled: true, event: 'both' }}
          />
        </div>
      </footer>

      {dialog === 'check' && (
        <CheckDialog statuses={options.checkStatuses} destinations={options.checkDestinations} onCancel={() => setDialog(null)} onAdd={addNewCheck} />
      )}
      {dialog === 'picker' && (
        <CheckPickerDialog excludedIds={checks.filter((c) => c.checkId).map((c) => c.checkId!)} onCancel={() => setDialog(null)} onAdd={addPortfolioChecks} />
      )}
      {dialog === 'tax' && (
        <TaxDialog taxTypes={options.taxTypes} usedTaxIds={taxes.map((t) => t.taxId)} onCancel={() => setDialog(null)} onAdd={addTax} />
      )}
    </>
  );
}
